import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/utils/formatting";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

// Define the structure based on change_log table
type ChangeLogEntry = {
  id: number;
  version_id: number;
  provider_id: number;
  region_id: number;
  entity_type: string;
  entity_id: number;
  field_name: string;
  action_type: 'CREATE' | 'UPDATE' | 'DELETE';
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  updated_at: string;
};

interface CategoryChangesTableProps {
  changes?: ChangeLogEntry[];
  isLoading: boolean;
}

export function CategoryChangesTable({ changes, isLoading }: CategoryChangesTableProps) {
  const [categoryNames, setCategoryNames] = useState<Record<number, string>>({});

  // Filter to only show categories entity_type
  const categoryChanges = changes?.filter(change =>
    change.entity_type === 'categories'
  ) || [];

  // Get unique category IDs
  const categoryIds = [...new Set(categoryChanges.map(c => c.entity_id))];

  // Fetch category names from database and extract from deleted entity data
  useEffect(() => {
    const fetchCategoryNames = async () => {
      if (categoryIds.length === 0) return;

      // First try to get names from the categories table for existing categories
      const { data, error } = await supabase
        .from('categories')
        .select('id, name')
        .in('id', categoryIds);

      const nameMap: Record<number, string> = {};

      // Add names for existing categories
      if (!error && data) {
        data.forEach(category => {
          nameMap[category.id] = category.name;
        });
      }

      // For categories not found in the database (deleted), extract names from change log old_value
      const missingCategoryIds = categoryIds.filter(id => !nameMap[id]);
      if (missingCategoryIds.length > 0) {
        missingCategoryIds.forEach(categoryId => {
          // Find a change entry for this category that has old_value with entity data
          const entityChange = categoryChanges.find(change =>
            change.entity_id === categoryId &&
            change.field_name === 'entity' &&
            change.old_value
          );

          if (entityChange?.old_value) {
            try {
              const entityData = JSON.parse(entityChange.old_value);
              nameMap[categoryId] = entityData.name || `Category #${categoryId}`;
            } catch (error) {
              console.error('Failed to parse entity data for category:', categoryId, error);
              nameMap[categoryId] = `Category #${categoryId}`;
            }
          } else {
            nameMap[categoryId] = `Category #${categoryId}`;
          }
        });
      }

      setCategoryNames(nameMap);
    };

    fetchCategoryNames();
  }, [categoryIds.join(','), categoryChanges]);

  // Group changes by category and then by actual action type
  const groupedByCategory = categoryChanges.reduce((acc, change) => {
    const categoryId = change.entity_id;

    if (!acc[categoryId]) {
      acc[categoryId] = {
        categoryId,
        categoryName: categoryNames[categoryId] || `Category #${categoryId}`,
        createFields: {},
        updateFields: {},
        deleteFields: {},
        createTimestamp: null,
        updateTimestamp: null,
        deleteTimestamp: null,
        latestTimestamp: change.created_at
      };
    }

    // Track latest timestamp for this category
    if (new Date(change.created_at) > new Date(acc[categoryId].latestTimestamp)) {
      acc[categoryId].latestTimestamp = change.created_at;
    }

    // Categorize changes based on old_value and new_value
    if (change.old_value === null && change.new_value !== null) {
      // Field was created (added)
      acc[categoryId].createFields[change.field_name] = {
        value: change.new_value
      };
      if (!acc[categoryId].createTimestamp || new Date(change.created_at) > new Date(acc[categoryId].createTimestamp!)) {
        acc[categoryId].createTimestamp = change.created_at;
      }
    } else if (change.old_value !== null && change.new_value === null) {
      // Field was deleted (removed)
      acc[categoryId].deleteFields[change.field_name] = {
        value: change.old_value
      };
      if (!acc[categoryId].deleteTimestamp || new Date(change.created_at) > new Date(acc[categoryId].deleteTimestamp!)) {
        acc[categoryId].deleteTimestamp = change.created_at;
      }
    } else if (change.old_value !== null && change.new_value !== null) {
      // Field was updated
      if (!acc[categoryId].updateFields[change.field_name]) {
        acc[categoryId].updateFields[change.field_name] = {
          oldValue: change.old_value,
          newValue: change.new_value
        };
      } else {
        // If field was already updated, keep the original old value but update the new value
        acc[categoryId].updateFields[change.field_name].newValue = change.new_value;
      }
      if (!acc[categoryId].updateTimestamp || new Date(change.created_at) > new Date(acc[categoryId].updateTimestamp!)) {
        acc[categoryId].updateTimestamp = change.created_at;
      }
    }

    return acc;
  }, {} as Record<number, {
    categoryId: number;
    categoryName: string;
    createFields: Record<string, { value: string }>;
    updateFields: Record<string, { oldValue: string; newValue: string }>;
    deleteFields: Record<string, { value: string }>;
    createTimestamp: string | null;
    updateTimestamp: string | null;
    deleteTimestamp: string | null;
    latestTimestamp: string;
  }>);

  if (isLoading) return <div>Loading...</div>;

  // Convert to array and sort
  const sortedCategories = Object.values(groupedByCategory).sort((a, b) =>
    new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime()
  );

  const renderFieldValue = (
    actionType: 'CREATE' | 'UPDATE' | 'DELETE',
    _fieldName: string,
    fieldData: any
  ) => {
    if (!fieldData) return <span className="text-muted-foreground">-</span>;

    // Regular field handling
    if (actionType === 'CREATE') {
      return (
        <div className="text-xs text-green-600 font-medium">
          {fieldData.value || 'null'}
        </div>
      );
    } else if (actionType === 'UPDATE') {
      return (
        <div className="space-y-1">
          <div className="text-xs text-red-600 line-through">
            {fieldData.oldValue || 'null'}
          </div>
          <div className="text-xs text-green-600 font-medium">
            {fieldData.newValue || 'null'}
          </div>
        </div>
      );
    } else if (actionType === 'DELETE') {
      return (
        <div className="text-xs text-red-600 line-through">
          {fieldData.value || 'null'}
        </div>
      );
    }

    return <span className="text-muted-foreground">-</span>;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Category Name</TableHead>
          <TableHead className="font-semibold">Action</TableHead>
          <TableHead className="font-semibold">Name</TableHead>
          <TableHead className="font-semibold">Description</TableHead>
          <TableHead className="font-semibold">Sort Order</TableHead>
          <TableHead className="font-semibold">Parent Category ID</TableHead>
          <TableHead className="font-semibold">Timestamp</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedCategories.map((category) => {
          // Build array of actions to display
          const actions = [];

          // Add CREATE row if there are created fields
          if (Object.keys(category.createFields).length > 0) {
            actions.push({
              type: 'CREATE' as const,
              fields: category.createFields,
              timestamp: category.createTimestamp
            });
          }

          // Add UPDATE row if there are updated fields
          if (Object.keys(category.updateFields).length > 0) {
            actions.push({
              type: 'UPDATE' as const,
              fields: category.updateFields,
              timestamp: category.updateTimestamp
            });
          }

          // Add DELETE row if there are deleted fields
          if (Object.keys(category.deleteFields).length > 0) {
            actions.push({
              type: 'DELETE' as const,
              fields: category.deleteFields,
              timestamp: category.deleteTimestamp
            });
          }

          if (actions.length === 0) return null;

          return actions.map((action, index) => {
            const isFirstRow = index === 0;

            return (
              <TableRow key={`${category.categoryId}-${action.type}`} className="hover:bg-muted/50">
                {/* Category Name - merged cell for first row only */}
                {isFirstRow && (
                  <TableCell
                    rowSpan={actions.length}
                    className="font-medium align-top border-r"
                  >
                    <div className="font-semibold">{category.categoryName}</div>
                  </TableCell>
                )}

                {/* Action */}
                <TableCell>
                  <Badge className={
                    action.type === 'CREATE' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" :
                    action.type === 'UPDATE' ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100" :
                    "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                  }>
                    {action.type}
                  </Badge>
                </TableCell>

                {/* Name */}
                <TableCell>
                  {renderFieldValue(action.type, 'name', action.fields['name'])}
                </TableCell>

                {/* Description */}
                <TableCell>
                  {renderFieldValue(action.type, 'description', action.fields['description'])}
                </TableCell>

                {/* Sort Order */}
                <TableCell>
                  {renderFieldValue(action.type, 'sort_order', action.fields['sort_order'])}
                </TableCell>

                {/* Parent Category ID */}
                <TableCell>
                  {renderFieldValue(action.type, 'parent_category_id', action.fields['parent_category_id'])}
                </TableCell>

                {/* Timestamp */}
                <TableCell>
                  <div className="text-xs">{action.timestamp ? formatDateTime(action.timestamp) : '-'}</div>
                </TableCell>
              </TableRow>
            );
          });
        })}
      </TableBody>
    </Table>
  );
}